import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useT } from '../i18n';
import { createAddTableCommand } from '../schema/commands';
import { createDefaultTable } from '../schema/defaults';
import { useSchemaStore } from '../schema/store';
import { useWorkspaceSelection } from './selection';
import { buildTreeEntries, type TreeTableEntry } from './treeFilter';

function toggleMembership(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((candidate) => candidate !== id) : [...ids, id];
}

/**
 * Levý panel (E11-US1/US2/US3/US4) — Schéma → Tabulky → Sloupce. Skupiny a
 * poznámky se sem záměrně nedostávají (epic-11 kap. 2: vybírají se jen na
 * canvasu), proto tento strom pracuje jen s `selectedTableIds`/tabulkovou
 * i sloupcovou částí `inspectorTarget`. "Přidat tabulku" (E4-US1) je jeden ze
 * dvou povinných vstupních bodů AC — druhý je kontextové menu prázdné
 * plochy canvasu (`CanvasView.tsx`).
 */
export function NavigationTree() {
  const t = useT();
  const { schema, execute: schemaExecute } = useSchemaStore();
  const { selectedTableIds, inspectorTarget, setSelectedTableIds, setInspectorTarget, requestFocus } =
    useWorkspaceSelection();
  const [query, setQuery] = useState('');
  const [expandedTableIds, setExpandedTableIds] = useState<string[]>([]);
  const lastClickedTableId = useRef<string | null>(null);

  const entries = useMemo(() => buildTreeEntries(schema.tables, query), [schema.tables, query]);
  const isFiltering = query.trim().length > 0;

  const isExpanded = (tableId: string) =>
    isFiltering ||
    expandedTableIds.includes(tableId) ||
    (inspectorTarget.kind === 'column' && inspectorTarget.tableId === tableId);

  const toggleExpanded = (tableId: string) =>
    setExpandedTableIds((previous) => toggleMembership(previous, tableId));

  const handleTableClick = (entry: TreeTableEntry, event: React.MouseEvent) => {
    const tableId = entry.table.id;
    if (event.shiftKey && lastClickedTableId.current) {
      const order = entries.map((candidate) => candidate.table.id);
      const from = order.indexOf(lastClickedTableId.current);
      const to = order.indexOf(tableId);
      if (from >= 0 && to >= 0) {
        const [start, end] = from < to ? [from, to] : [to, from];
        const range = order.slice(start, end + 1);
        const next = Array.from(new Set([...selectedTableIds, ...range]));
        setSelectedTableIds(next);
        setInspectorTarget(next.length === 1 ? { kind: 'table', tableId: next[0] } : { kind: 'none' });
        return;
      }
    }
    if (event.ctrlKey || event.metaKey) {
      const next = toggleMembership(selectedTableIds, tableId);
      setSelectedTableIds(next);
      setInspectorTarget(next.length === 1 ? { kind: 'table', tableId: next[0] } : { kind: 'none' });
      lastClickedTableId.current = tableId;
      return;
    }
    requestFocus({ kind: 'table', tableId });
    lastClickedTableId.current = tableId;
  };

  const addTable = () => {
    const table = createDefaultTable(schema);
    schemaExecute(createAddTableCommand(table));
    requestFocus({ kind: 'table', tableId: table.id });
  };

  return (
    <div className="nav-tree">
      <div className="nav-tree__search">
        <input
          type="search"
          value={query}
          placeholder={t('workspace.tree.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
          aria-label={t('workspace.tree.searchPlaceholder')}
        />
        <button type="button" onClick={addTable} title={t('schema.table.addAction')}>
          <Plus size={16} />
          {t('schema.table.addAction')}
        </button>
      </div>

      <div className="nav-tree__list" role="tree">
        {schema.tables.length === 0 && <p className="hint nav-tree__empty">{t('workspace.tree.empty')}</p>}
        {schema.tables.length > 0 && isFiltering && entries.length === 0 && (
          <p className="hint nav-tree__empty">{t('workspace.tree.noMatches', { query })}</p>
        )}

        {entries.map((entry) => {
          const tableId = entry.table.id;
          const expanded = isExpanded(tableId);
          const isTableSelected = selectedTableIds.includes(tableId);
          return (
            <div key={tableId} role="group">
              <div
                className={
                  isTableSelected ? 'nav-tree__row nav-tree__row--table nav-tree__row--selected' : 'nav-tree__row nav-tree__row--table'
                }
              >
                <button
                  type="button"
                  className="nav-tree__chevron"
                  aria-label={expanded ? t('workspace.panel.collapse') : t('workspace.panel.expand')}
                  onClick={() => toggleExpanded(tableId)}
                >
                  {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <button
                  type="button"
                  role="treeitem"
                  aria-selected={isTableSelected}
                  className="nav-tree__label"
                  onClick={(event) => handleTableClick(entry, event)}
                >
                  {entry.table.name}
                </button>
                <span className="nav-tree__count" title={t('workspace.tree.columnsTooltip', { count: entry.table.columns.length })}>
                  {entry.table.columns.length}
                </span>
              </div>

              {expanded && (
                <div className="nav-tree__columns">
                  {entry.columns.map((column) => {
                    const isColumnSelected =
                      inspectorTarget.kind === 'column' &&
                      inspectorTarget.tableId === tableId &&
                      inspectorTarget.columnId === column.id;
                    return (
                      <button
                        key={column.id}
                        type="button"
                        role="treeitem"
                        aria-selected={isColumnSelected}
                        className={
                          isColumnSelected
                            ? 'nav-tree__row nav-tree__row--column nav-tree__row--selected'
                            : 'nav-tree__row nav-tree__row--column'
                        }
                        onClick={() => requestFocus({ kind: 'column', tableId, columnId: column.id })}
                      >
                        <span className="nav-tree__label">{column.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
